import AxiosInstance from '../../helpers/axios-instance/axios-instance';
import { appConfig } from '../../helpers/app-config/app-config';
import type {
  TeamsChannelsResponse,
  TeamsInstallStatus,
  TeamsOAuthStartResponse,
} from './teams-integrations-client.type';

const axiosInstance = AxiosInstance();
const baseURL = `${appConfig.VITE_APP_API_URL}/integrations/teams`;

/**
 * Fetch the AAD admin-consent URL that begins the org-tenant install flow.
 *
 * The BE returns the URL as JSON (not a 302) so the call can carry our JWT
 * `Authorization` header — top-level navigation cannot. The popup is then
 * navigated to this URL.
 *
 * @param {string} organizationId - The organization initiating the install.
 * @returns {Promise<string>} The AAD admin-consent URL to navigate the popup to.
 */
function getInstallStartUrl(organizationId: string): Promise<string> {
  const url = `${baseURL}/install/start`;
  return axiosInstance
    .get<TeamsOAuthStartResponse>(url, { params: { organization_id: organizationId } })
    .then((response) => response.data.url)
    .catch((error) => {
      const message = error.response?.data?.detail || error.message || 'Failed to start Teams install';
      console.error('[Teams] getInstallStartUrl error:', message, error.response?.data);
      throw new Error(message);
    });
}

/**
 * Fetch the AAD authorize URL that begins the per-user OAuth flow.
 *
 * @param {string} organizationId - The organization the user is connecting to.
 * @returns {Promise<string>} The AAD authorize URL to navigate the popup to.
 */
function getUserAuthStartUrl(organizationId: string): Promise<string> {
  const url = `${baseURL}/user-auth/start`;
  return axiosInstance
    .get<TeamsOAuthStartResponse>(url, { params: { organization_id: organizationId } })
    .then((response) => response.data.url)
    .catch((error) => {
      const message = error.response?.data?.detail || error.message || 'Failed to start Teams connect';
      console.error('[Teams] getUserAuthStartUrl error:', message, error.response?.data);
      throw new Error(message);
    });
}

/**
 * Fetch install + per-user-connect status for an organization.
 *
 * @param {string} organizationId - The organization to check.
 * @returns {Promise<TeamsInstallStatus>} Status object including installed/tenant_id/tenant_name/bot_id/user_connected.
 */
function getTeamsStatus(organizationId: string): Promise<TeamsInstallStatus> {
  const url = `${baseURL}/status`;
  return axiosInstance
    .get<TeamsInstallStatus>(url, { params: { organization_id: organizationId } })
    .then((response) => response.data)
    .catch((error) => {
      const message = error.response?.data?.detail || error.message || 'Failed to fetch Teams status';
      console.error('[Teams] getTeamsStatus error:', message, error.response?.data);
      throw new Error(message);
    });
}

/**
 * List Teams channels visible to the current user across their joined teams.
 *
 * Requires the user to have completed per-user OAuth — the BE returns 409
 * Conflict when the user has not connected their Teams account.
 *
 * @param {string} organizationId - The organization whose Teams install to list channels in.
 * @returns {Promise<TeamsChannelsResponse>} Channels list.
 */
function getTeamsChannels(organizationId: string): Promise<TeamsChannelsResponse> {
  const url = `${baseURL}/channels`;
  return axiosInstance
    .get<TeamsChannelsResponse>(url, { params: { organization_id: organizationId } })
    .then((response) => response.data)
    .catch((error) => {
      const message = error.response?.data?.detail || error.message || 'Failed to load Teams channels';
      console.error('[Teams] getTeamsChannels error:', message, error.response?.data);
      throw new Error(message);
    });
}

/**
 * Soft-delete the org's Teams installation (admin only).
 *
 * @param {string} organizationId - The organization whose install to remove.
 * @returns {Promise<void>} Resolves when the install has been deactivated.
 */
async function uninstallTeams(organizationId: string): Promise<void> {
  const url = `${baseURL}/install`;
  await axiosInstance.delete(url, { params: { organization_id: organizationId } }).catch((error) => {
    const message = error.response?.data?.detail || error.message || 'Failed to uninstall Teams';
    console.error('[Teams] uninstallTeams error:', message, error.response?.data);
    throw new Error(message);
  });
}

/**
 * Disconnect the current user's Teams account from the given org.
 *
 * @param {string} organizationId - The organization whose user-auth to soft-delete.
 * @returns {Promise<void>} Resolves when the user-auth row has been soft-deleted.
 */
async function disconnectTeamsUser(organizationId: string): Promise<void> {
  const url = `${baseURL}/user-auth`;
  await axiosInstance.delete(url, { params: { organization_id: organizationId } }).catch((error) => {
    const message = error.response?.data?.detail || error.message || 'Failed to disconnect your Teams account';
    console.error('[Teams] disconnectTeamsUser error:', message, error.response?.data);
    throw new Error(message);
  });
}

export {
  disconnectTeamsUser,
  getInstallStartUrl,
  getTeamsChannels,
  getTeamsStatus,
  getUserAuthStartUrl,
  uninstallTeams,
};
